import axios from 'axios';

/**
 * Местоположение аптеки (PROJECT_NOTES §37): профиль под сессией и телефон по одноразовой ссылке.
 * Оба пути отвечают одинаково, поэтому мастер работает с интерфейсом GeoBackend и не знает, откуда он открыт.
 * Логин в запросах не передается: его берет сервер из сессии или из ссылки.
 */
const PROFILE_GEO_API = '/api/v2/profile/geolocation';
const HANDOFF_API = '/api/v2/geo-handoff';
const PHONE_PAGE = '/mobile-location';

export interface GeoCoords {
  latitude: number;
  longitude: number;
}

/** Замер браузера: точность - радиус в метрах */
export interface GeoMeasurement extends GeoCoords {
  accuracy: number;
}

/** Место из справочника адресов: short - «улица, дом, город», address - полный адрес (он и сохраняется) */
export interface GeoPlace extends GeoCoords {
  name: string;
  short: string;
  address: string;
}

export interface SavedGeo extends GeoCoords {
  accuracy: number | null;
  address: string;
  updatedAt?: string | null;
}

/** Что сохраняем: замер как есть (gps) или пин на карте (manual) - точность пина считает сервер по масштабу */
export type GeoPointInput =
  | { source: 'gps'; latitude: number; longitude: number; accuracy: number }
  | { source: 'manual'; latitude: number; longitude: number; zoom: number; measured: GeoMeasurement | null };

export interface SavedPoint extends GeoCoords {
  accuracy: number;
  source: 'gps' | 'manual';
  address: string;
  short: string;
}

export interface GeoBackend {
  /** null - адрес не определен (точку все равно можно сохранить) */
  address(point: GeoCoords, signal?: AbortSignal): Promise<GeoPlace | null>;
  search(q: string, near: GeoCoords | null): Promise<GeoPlace[]>;
  save(point: GeoPointInput): Promise<{ message: string; geolocation: SavedPoint }>;
}

export type HandoffStatus = 'pending' | 'opened' | 'completed' | 'expired';

export interface HandoffState {
  status: HandoffStatus;
  expiresAt: string;
  geolocation: SavedPoint | null;
}

export interface HandoffInfo {
  username: string;
  fullName: string;
  /** Срок ссылки по часам телефона */
  deadline: number;
  saved: SavedGeo | null;
}

// Срок по часам этого устройства: от остатка в секундах, а если сервер его не прислал - от даты сервера
function deadlineOf(secondsLeft: unknown, expiresAt: unknown): number {
  const seconds = Number(secondsLeft);
  return seconds > 0 ? Date.now() + seconds * 1000 : Date.parse(String(expiresAt));
}

function backendAt(base: string): GeoBackend {
  return {
    async address(point, signal) {
      const res = await axios.get(`${base}/address`, { params: { lat: point.latitude, lng: point.longitude }, signal });
      return (res.data && res.data.place) || null;
    },
    async search(q, near) {
      const params = near ? { q, lat: near.latitude, lng: near.longitude } : { q };
      const res = await axios.get(`${base}/search`, { params });
      if (!res.data || !res.data.success || !Array.isArray(res.data.places)) throw new Error('Сервер вернул адреса в неожиданном виде');
      return res.data.places;
    },
    async save(point) {
      const res = await axios.post(base, point);
      if (!res.data || !res.data.success || !res.data.geolocation) throw new Error('Сервер не подтвердил сохранение координат');
      return { message: res.data.message || 'Местоположение сохранено', geolocation: res.data.geolocation };
    }
  };
}

export const profileGeoBackend = backendAt(PROFILE_GEO_API);

export function handoffGeoBackend(token: string): GeoBackend {
  return backendAt(`${HANDOFF_API}/${encodeURIComponent(token)}`);
}

/** Сохраненная точка для запроса при входе: geolocation null - точки нет; impersonated - вход администратора */
export async function fetchSavedGeo(): Promise<{ geolocation: SavedGeo | null; impersonated: boolean }> {
  const res = await axios.get(PROFILE_GEO_API);
  if (!res.data || !res.data.success) throw new Error('Сервер вернул местоположение в неожиданном виде');
  return { geolocation: res.data.geolocation || null, impersonated: Boolean(res.data.impersonated) };
}

/**
 * Новая ссылка для телефона; прежняя перестает работать.
 * deadline - срок по часам этого компьютера: считается от ttlSec, чтобы отсчет не сбивали расходящиеся часы сервера
 */
export async function createHandoff(): Promise<{ token: string; deadline: number }> {
  const res = await axios.post(`${PROFILE_GEO_API}/handoff`);
  if (!res.data || !res.data.success || !res.data.token) throw new Error('Сервер не выдал ссылку для телефона');
  return { token: res.data.token, deadline: deadlineOf(res.data.ttlSec, res.data.expiresAt) };
}

export async function fetchHandoffStatus(token: string): Promise<HandoffState> {
  try {
    const res = await axios.get(`${PROFILE_GEO_API}/handoff/${encodeURIComponent(token)}`);
    if (!res.data || !res.data.success) throw new Error('Сервер вернул состояние ссылки в неожиданном виде');
    return { status: res.data.status, expiresAt: res.data.expiresAt, geolocation: res.data.geolocation || null };
  } catch (err) {
    // Ссылку заменила новая или сервер перезапустился и забыл ее - для экрана это то же, что истекший срок
    if (axios.isAxiosError(err) && err.response && err.response.status === 404) return { status: 'expired', expiresAt: '', geolocation: null };
    throw err;
  }
}

/** Телефон: для кого ставится точка; ошибка с message сервера - ссылка истекла или использована */
export async function fetchHandoffInfo(token: string): Promise<HandoffInfo> {
  const res = await axios.get(`${HANDOFF_API}/${encodeURIComponent(token)}`);
  if (!res.data || !res.data.success) throw new Error('Сервер вернул ссылку в неожиданном виде');
  return {
    username: res.data.username,
    fullName: res.data.fullName || '',
    deadline: deadlineOf(res.data.secondsLeft, res.data.expiresAt),
    saved: res.data.saved || null
  };
}

/**
 * Адрес страницы для QR-кода. Телефон дает координаты только странице на https, поэтому с локального http-адреса
 * ссылка ведет на REACT_APP_PHONE_ORIGIN (внешний адрес портала, например https://dev.zerdegroup.kz)
 */
export function phoneLink(token: string): string {
  const configured = (process.env.REACT_APP_PHONE_ORIGIN || '').replace(/\/+$/, '');
  const origin = window.location.protocol === 'https:' || !configured ? window.location.origin : configured;
  return `${origin}${PHONE_PAGE}?token=${encodeURIComponent(token)}`;
}
